import React, { useEffect, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  apiService,
  AffiliateMemberPayload,
  User,
  AffiliateMemberStatus,
  PaymentMethod,
} from '../services/api';

interface AffiliateMemberCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AffiliateMemberCreateModal: React.FC<AffiliateMemberCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [formData, setFormData] = useState<{
    userId: number | null;
    walletBalance: string;
    paymentMethodId: number | null;
    accountNumber: string;
    status: AffiliateMemberStatus;
  }>({
    userId: null,
    walletBalance: '0',
    paymentMethodId: null,
    accountNumber: '',
    status: AffiliateMemberStatus.ACTIVE,
  });

  useEffect(() => {
    if (visible) {
      fetchUsers();
      fetchPaymentMethods();
    }
  }, [visible]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getUsers({ limit: 100 });
      setUsers(response.users ?? []);
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiService.getPaymentMethods();
      setPaymentMethods(response);
    } catch (err) {
      console.error('Failed to fetch payment methods', err);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!userSearchTerm) return true;
    const keyword = userSearchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(keyword) ||
      user.name?.toLowerCase().includes(keyword) ||
      user.username?.toLowerCase().includes(keyword)
    );
  });

  const selectedUser = users.find((u) => u.id === formData.userId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.userId ||
      !formData.paymentMethodId ||
      !formData.accountNumber
    ) {
      alert('Please complete all required fields');
      return;
    }

    try {
      setLoading(true);

      const payload: AffiliateMemberPayload = {
        userId: formData.userId,
        walletBalance: parseFloat(formData.walletBalance) || 0,
        status: formData.status,
        paymentMethodId: formData.paymentMethodId,
        accountNumber: formData.accountNumber,
      };

      await apiService.createAffiliateMember(payload);

      resetForm();
      onSuccess();
    } catch (error: any) {
      console.error(error);
      alert(error.message || 'Failed to create affiliate member');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      userId: null,
      walletBalance: '0',
      paymentMethodId: null,
      accountNumber: '',
      status: AffiliateMemberStatus.ACTIVE,
    });
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Create Affiliate Member</h2>
          <button onClick={onCancel}>
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {/* USER */}
          <div>
            <label className="text-sm font-medium">User</label>
            <div className="relative mt-1">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                value={userSearchTerm}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value);
                  setShowUserDropdown(true);
                  if (!e.target.value) {
                    setFormData((p) => ({ ...p, userId: null }));
                  }
                }}
                onFocus={() => setShowUserDropdown(true)}
                placeholder="Search user..."
                disabled={loadingUsers}
                className="w-full pl-9 pr-3 py-2 border rounded-md"
              />

              {showUserDropdown && (
                <div className="absolute z-20 bg-white border rounded-md mt-1 w-full max-h-48 overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => {
                        setFormData((p) => ({ ...p, userId: user.id }));
                        setUserSearchTerm(user.name || user.email || '');
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100"
                    >
                      <div className="text-sm font-medium">
                        {user.name || 'No name'}
                      </div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedUser && (
              <div className="mt-2 text-sm text-gray-600">
                {selectedUser.name} ({selectedUser.email})
              </div>
            )}
          </div>

          {/* WALLET */}
          <div>
            <label className="text-sm font-medium">Wallet Balance</label>
            <input
              type="number"
              value={formData.walletBalance}
              min={0}
              onChange={(e) =>
                setFormData((p) => ({ ...p, walletBalance: e.target.value }))
              }
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* PAYMENT METHOD */}
          <div>
            <label className="text-sm font-medium">Payment Method</label>
            <select
              value={formData.paymentMethodId ?? ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  paymentMethodId: e.target.value
                    ? parseInt(e.target.value)
                    : null,
                }))
              }
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Payment Method</option>
              {paymentMethods.map((pm) => (
                <option key={pm.id} value={pm.id}>
                  {pm.name}
                </option>
              ))}
            </select>
          </div>

          {/* ACCOUNT NUMBER */}
          <div>
            <label className="text-sm font-medium">Account Number</label>
            <input
              type="number"
              value={formData.accountNumber}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  accountNumber: e.target.value,
                }))
              }
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* STATUS */}
          <div>
            <label className="text-sm font-medium">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  status: e.target.value as AffiliateMemberStatus,
                }))
              }
              className="w-full border rounded-md px-3 py-2"
            >
              <option value={AffiliateMemberStatus.ACTIVE}>Active</option>
              <option value={AffiliateMemberStatus.INACTIVE}>Inactive</option>
              <option value={AffiliateMemberStatus.BANNED}>Banned</option>
            </select>
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-imola-primary text-white rounded-md disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateMemberCreateModal;
